import React, { useState, useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import Pagination from "../../common/Pagination";
import { formatDate } from "../../../utils/dateUtils";
import FullScreenOverlay from "../../common/FullScreenOverlay";
import {
  SearchIcon,
  SortArrowIcon,
  DeleteIcon,
  PlusIcon,
  EyeIcon,
} from "../../common/Icons";
import "../CarePlan/CarePlans.css";
import "../MDTUser/MDTUsers.css";
import { useDebounce } from "../../../hooks/useDebounce";
import IconButton from "../../common/Button";
import { fetchCarePlanMdtUsers } from "../../../redux/actions/userActions";
import {
  setMdtFilters,
  setAssignedPage,
  setAssignedRowsPerPage,
  setUnassignedPage,
  setUnassignedRowsPerPage,
} from "../../../redux/slices/userSlice";
import {
  addUsersToCarePlan,
  removeUserFromCarePlan,
} from "../../../redux/actions/careplanActions";

const CarePlanMDTUsers = ({ selectedCarePlan }) => {
  const dispatch = useDispatch();

  // Selectors for care plan mdt users
  const {
    assigned_users,
    unassigned_users,
    assigned_pagination,
    unassigned_pagination,
    assignedPage,
    assignedRowsPerPage,
    unassignedPage,
    unassignedRowsPerPage,
    searchQuery,
  } = useSelector((s) => s.users.mdtUsersData);
  const loading = useSelector((s) => s.users.mdtUsersLoading);
  const error = useSelector((s) => s.users.mdtUsersError);

  // Local component states
  const [searchInput, setSearchInput] = useState("");
  const [addView, setAddView] = useState(false);
  const [selectedUser, setSelectedUser] = useState({});
  const [sortField, setSortField] = useState("");
  const [sortOrder, setSortOrder] = useState("asc");
  const [showAddUserConfirmation, setShowAddUserConfirmation] = useState(false);
  const [showDeleteUserConfirmation, setShowDeleteUserConfirmation] =
    useState(false);

  const debounced = useDebounce(searchInput, 500);

  useEffect(() => {
    dispatch(
      setMdtFilters(
        debounced
          ? { name: debounced, email: debounced }
          : { name: "", email: "" }
      )
    );
  }, [debounced, dispatch]);

  // Fetch users when filters (or other pagination/search parameters) update
  useEffect(() => {
    const orderingParam = sortField
      ? sortOrder === "asc"
        ? sortField
        : `-${sortField}`
      : "";
    dispatch(
      fetchCarePlanMdtUsers({
        carePlanId: selectedCarePlan.id,
        assignedPage,
        assignedRowsPerPage,
        unassignedPage,
        unassignedRowsPerPage,
        search: searchQuery.name || searchQuery.email || "",
        ordering: orderingParam,
      })
    );
  }, [
    dispatch,
    selectedCarePlan.id,
    assignedPage,
    assignedRowsPerPage,
    unassignedPage,
    unassignedRowsPerPage,
    searchQuery.name,
    searchQuery.email,
    sortField,
    sortOrder,
  ]);

  const list = addView ? unassigned_users : assigned_users;
  const meta = addView ? unassigned_pagination : assigned_pagination;
  const page = addView ? unassignedPage : assignedPage;
  const perPg = addView ? unassignedRowsPerPage : assignedRowsPerPage;

  const handleSort = (field) => {
    if (sortField === field) {
      setSortOrder((prev) => (prev === "asc" ? "desc" : "asc"));
    } else {
      setSortField(field);
      setSortOrder("asc");
    }
  };

  const handleConfirmSave = (user) => {
    setSelectedUser(user);
    setShowAddUserConfirmation(true);
  };

  const saveMdtUser = async () => {
    // Transform the selectedMdtUsers into the desired backend needed format
    const userAssignments = [
      {
        user_id: selectedUser.id,
        can_edit: true, // If can edit is provided then the value will be taken else true will be assigned
      },
    ];
    // Hide the confirmation modal once the user has confirmed before the API call
    setShowAddUserConfirmation(false);
    try {
      // Dispatch the action to add users to the care plan
      const resultAction = await dispatch(
        addUsersToCarePlan({ carePlanId: selectedCarePlan.id, userAssignments })
      );

      if (addUsersToCarePlan.fulfilled.match(resultAction)) {
        const orderingParam = sortField
          ? sortOrder === "asc"
            ? sortField
            : `-${sortField}`
          : "";
        dispatch(
          fetchCarePlanMdtUsers({
            carePlanId: selectedCarePlan.id,
            assignedPage,
            assignedRowsPerPage,
            unassignedPage,
            unassignedRowsPerPage,
            search: searchQuery.name || searchQuery.email || "",
            ordering: orderingParam,
          })
        );
      } else {
        // Handle the error case
        console.error("Failed to add MDT users:", resultAction.payload);
        // Optionally, show an error notification to the user
      }
    } catch (error) {
      console.error("Error saving MDT users:", error);
      // Handle error (e.g., show an error notification)
    }
  };

  // delete confirmation
  const handleConfirmDelete = (user) => {
    setSelectedUser(user);
    setShowDeleteUserConfirmation(true);
  };

  const deleteMdtUser = async () => {
    setShowDeleteUserConfirmation(false); // Close delete confirmation popup
    try {
      const resultAction = await dispatch(
        removeUserFromCarePlan({
          carePlanId: selectedCarePlan.id,
          userId: selectedUser.id,
        })
      );

      if (removeUserFromCarePlan.fulfilled.match(resultAction)) {
        const orderingParam = sortField
          ? sortOrder === "asc"
            ? sortField
            : `-${sortField}`
          : "";
        dispatch(
          fetchCarePlanMdtUsers({
            carePlanId: selectedCarePlan.id,
            assignedPage,
            assignedRowsPerPage,
            unassignedPage,
            unassignedRowsPerPage,
            search: searchQuery.name || searchQuery.email || "",
            ordering: orderingParam,
          })
        );
      } else {
        console.error("Failed to remove MDT user:", resultAction.payload);
      }
    } catch (error) {
      console.error("Error removing MDT user:", error);
    }
  };

  if (loading) {
    return <div>Loading...</div>;
  }

  if (error) {
    return <div>Error: {error}</div>;
  }

  return (
    <div className="draftplan-container">
      <div className="search-filter-bar">
        <div className="left-section">
          <div className="search-bar">
            <div className="search-icon">
              <SearchIcon />
            </div>
            <input
              type="text"
              placeholder="Search user by name or email"
              value={searchInput}
              onChange={(e) => setSearchInput(e.target.value)}
            />
          </div>
        </div>
        <div className="right-section">
          <IconButton
            onClick={() => setAddView((v) => !v)}
            icon={
              addView ? (
                <EyeIcon color="#FFFFFF" />
              ) : (
                <PlusIcon color="#FFFFFF" />
              )
            }
          >
            {addView ? "View Assigned" : "Add Users"}
          </IconButton>
        </div>
      </div>

      <div className="table-wrapper">
        <table className="care-plan-table">
          <thead>
            <tr>
              {[
                ["profile__name", "Name"],
                ["profile__mdt_roles__name", "Job Role"],
                ["profile__last_active", "Last active"],
              ].map(([field, label]) => (
                <th key={field}>
                  <div
                    style={{ cursor: "pointer" }}
                    onClick={() => handleSort(field)}
                  >
                    {label} <SortArrowIcon />
                  </div>
                </th>
              ))}
              <th />
            </tr>
          </thead>
          <tbody>
            {list.map((u) => (
              <tr key={u.id}>
                <td>
                  <div className="body-text-regular medium neutral-900">
                    {u.profile?.name || u.name}
                  </div>
                  <div className="body-text-small regular neutral-700">
                    {u.email}
                  </div>
                </td>
                <td>{(u.mdt_roles || []).join(", ")}</td>
                <td>{u.last_active ? formatDate(u.last_active) : ""}</td>
                <td>
                  {addView ? (
                    <IconButton
                      variant="ghost"
                      icon={<PlusIcon color="#004F96" />}
                      onClick={() => handleConfirmSave(u)}
                    >
                      Assign user
                    </IconButton>
                  ) : (
                    <IconButton
                      variant="ghost"
                      isRemove={true}
                      icon={<DeleteIcon color="#EC2D30" />}
                      onClick={() => handleConfirmDelete(u)}
                    >
                      Remove user
                    </IconButton>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <Pagination
        currentPage={page}
        rowsPerPage={perPg}
        totalRows={meta.count}
        totalPages={Math.ceil(meta.count / perPg)}
        onPageChange={(p) =>
          addView
            ? dispatch(setUnassignedPage(p))
            : dispatch(setAssignedPage(p))
        }
        onRowsPerPageChange={(s) =>
          addView
            ? dispatch(setUnassignedRowsPerPage(s))
            : dispatch(setAssignedRowsPerPage(s))
        }
      />

      {showAddUserConfirmation && (
        <FullScreenOverlay
          width="20%"
          height="20%"
          isPopup={true}
          popupType="confirmation"
          buttons={[
            {
              label: "No, Cancel",
              onClick: () => setShowAddUserConfirmation(false),
            },
            {
              label: "Yes, Assign",
              onClick: saveMdtUser,
              variant: "primary",
            },
          ]}
        >
          <h5 className="semi-bold">Add users?</h5>
          <p>Are you sure you want to add the users to this plan?</p>
        </FullScreenOverlay>
      )}

      {/* Confirm Delete Care Plan Popup */}
      {showDeleteUserConfirmation && (
        <FullScreenOverlay
          zIndex={1100}
          width="480px"
          height="300px"
          isPopup={true}
          popupType="delete"
          buttons={[
            {
              label: "No, Cancel",
              onClick: () => {
                setShowDeleteUserConfirmation(false);
              },
            },
            {
              label: "Yes, Remove",
              onClick: deleteMdtUser, // Handle delete confirmation
              variant: "danger",
              state: "idle",
            },
          ]}
        >
          <h5 className="semi-bold">Remove care plan access?</h5>
          <p>
            Are you sure you want to remove the access of this user from this
            care plan?
          </p>
        </FullScreenOverlay>
      )}
    </div>
  );
};

export default CarePlanMDTUsers;
